import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clubsAPI, venuesAPI, profileAPI } from '../lib/api';

const PENDING_KEY = 'loveall:pending_owner_signup';

// Reads & validates the pre-OAuth owner-signup payload. Returns null if it's
// missing, expired, or for a non-owner role.
const readPendingOwnerSignup = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object') return null;
    if (Number(p.expires_at) < Date.now()) return null;
    if (!['club_admin', 'venue_admin', 'school_admin'].includes(p.role)) return null;
    return p;
  } catch { return null; }
};

const clearPendingOwnerSignup = () => {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
};

export default function AuthCallback() {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [status, setStatus] = useState('Completing sign in...');

  useEffect(() => {
    const fullHash = window.location.hash;
    const sessionId = fullHash.split('session_id=')[1]?.split('&')[0];

    if (sessionId && !processed.current) {
      processed.current = true;

      const applyPendingOwnerProfile = async (pending) => {
        // 1. Upgrade the user's role (profile endpoint already validates that
        //    only PUBLIC_SIGNUP_ROLES can be self-assigned).
        try {
          await profileAPI.update({ role: pending.role });
        } catch (err) {
          console.warn('[AuthCallback] role update failed:', err?.response?.data?.detail || err?.message);
          return;
        }
        // 2. Provision a starter Club or Venue with the chosen org name.
        if (!pending.org_name) return;
        try {
          if (pending.role === 'venue_admin') {
            await venuesAPI.create({
              name: pending.org_name,
              address: '', courts: 1,
              description: 'Created during owner signup (Google)',
              hourly_rate: 0, session_rate: 0,
            });
          } else {
            await clubsAPI.create({
              name: pending.org_name,
              description: 'Created during owner signup (Google)',
              location: '', sport_focus: 'badminton',
            });
          }
        } catch (err) {
          console.warn('[AuthCallback] starter org provisioning skipped:', err?.response?.data?.detail || err?.message);
        }
      };

      const doExchange = async (retries = 3) => {
        for (let attempt = 0; attempt < retries; attempt++) {
          try {
            setStatus(attempt > 0 ? `Retrying... (${attempt + 1}/${retries})` : 'Completing sign in...');
            await exchangeSession(sessionId);
            window.history.replaceState(null, '', window.location.pathname);

            // If this OAuth round-trip originated from /owner-register, apply
            // the pending role + org now that we have a valid session.
            const pending = readPendingOwnerSignup();
            if (pending) {
              setStatus('Setting up your club…');
              await applyPendingOwnerProfile(pending);
              clearPendingOwnerSignup();
            }

            navigate('/dashboard', { replace: true });
            return;
          } catch (error) {
            console.error(`[AuthCallback] Attempt ${attempt + 1} failed:`, error?.response?.status);
            if (attempt < retries - 1) {
              await new Promise(r => setTimeout(r, 1500));
            }
          }
        }
        setStatus('Sign in failed. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      };

      doExchange();
    } else if (!sessionId) {
      navigate('/login', { replace: true });
    }
  }, [exchangeSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center px-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground text-lg">{status}</p>
      </div>
    </div>
  );
}
